/**
 * Dynamic Product Description
 *
 * Shows the authored "base" bullet list until the customer has fully
 * configured the product, then swaps it for a list built from the selected
 * variant's option values + relevant metafields. The base list is hidden
 * (never removed) so it returns if the customer clears a selection or the
 * section re-renders. Listens for native variant changes and overflow
 * picker matches.
 *
 * Data source: <script data-dynamic-description-data> emitted by
 * snippets/dynamic-description-data.liquid (rendered once per PDP section).
 *
 * Description target: the first rte-formatter inside the product details
 * column (data-testid="product-information-details"). Matches the same
 * selector used by sdx-variant-swap.js.
 */
(function () {
  'use strict';

  /** Marker so we know which <ul> we own and can replace on update. */
  const MARKER_CLASS = 'dynamic-description-bullets';

  /** @returns {HTMLElement|null} */
  function getDescriptionEl(section) {
    const root = section || document;
    return root.querySelector(
      '[data-testid="product-information-details"] rte-formatter.rte'
    );
  }

  /** @returns {HTMLScriptElement|null} */
  function getDataScript(section) {
    return section.querySelector('script[data-dynamic-description-data]');
  }

  /** Parse the data script. Returns null on failure. */
  function readData(section) {
    const script = getDataScript(section);
    if (!script || !script.textContent.trim()) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      console.error('[dynamic-description] failed to parse data:', e);
      return null;
    }
  }

  /**
   * Build the dynamic <ul> for a given variant.
   * Returns null if there's nothing meaningful to show.
   *
   * @param {string|number} variantId
   * @param {object} data
   * @returns {HTMLUListElement|null}
   */
  function buildBulletList(variantId, data) {
    const variant = data.variants[String(variantId)];
    if (!variant) return null;

    const skipOptionNames = new Set(data.skipOptionNames || []);
    const optionNames = data.optionNames || [];
    const metafieldLabels = data.metafieldLabels || {};

    /** @type {Array<{label: string, value: string}>} */
    const rows = [];
    const seenValues = new Set();

    function pushRow(label, value) {
      if (value == null) return;
      const trimmed = String(value).trim();
      if (!trimmed) return;
      // Dedupe by lowercased value — same spec can live in both an
      // option and a metafield; show it once.
      const key = trimmed.toLowerCase();
      if (seenValues.has(key)) return;
      seenValues.add(key);
      rows.push({ label: label, value: trimmed });
    }

    // Native option values, in option order, skipping configured-out names.
    (variant.options || []).forEach((value, idx) => {
      const name = optionNames[idx];
      if (!name) return;
      if (skipOptionNames.has(name)) return;
      pushRow(name, value);
    });

    // Metafields, in the order keys appear in the data.
    Object.keys(variant.metafields || {}).forEach((key) => {
      const label = metafieldLabels[key] || key;
      pushRow(label, variant.metafields[key]);
    });

    if (rows.length === 0) return null;

    const ul = document.createElement('ul');
    ul.className = MARKER_CLASS;
    rows.forEach((row) => {
      const li = document.createElement('li');
      const strong = document.createElement('strong');
      strong.textContent = row.label + ': ';
      li.appendChild(strong);
      li.appendChild(document.createTextNode(row.value));
      ul.appendChild(li);
    });
    return ul;
  }

  /**
   * Whether the dynamic bullet list should render at all.
   *
   * Single-variant products: always show — there's nothing to choose.
   * Multi-variant products: only after EVERY picker is filled in.
   *
   * URL `?variant=…` is unreliable as a "fully selected" signal because
   * Shopify's `product_option_value.variant` resolves to the first
   * matching variant for partial selections, so the variant-picker can
   * write `?variant=N` after a single-option pick on some products.
   * Instead we directly inspect picker state:
   *   - every native fieldset has a checked radio
   *   - every native dropdown has a non-empty value
   *   - every overflow metafield/config dropdown has a non-empty value
   */
  function shouldShowBullets(section) {
    const picker = section.querySelector('variant-picker');
    if (!picker) return true; // no picker → single-variant product

    const fieldsets = picker.querySelectorAll('fieldset');
    for (const fs of fieldsets) {
      if (!fs.querySelector('input[type="radio"]:checked')) return false;
    }

    const selects = picker.querySelectorAll('.variant-option__select');
    for (const sel of selects) {
      if (!sel.value) return false;
    }

    const overflow = section.querySelector('overflow-variant-picker');
    if (overflow) {
      const overflowSelects = overflow.querySelectorAll(
        '.overflow-metafield-select, .overflow-config-select'
      );
      for (const sel of overflowSelects) {
        if (!sel.value) return false;
      }
    }

    return true;
  }

  /**
   * Find the authored "base" bullet list — the trailing authored <ul> the
   * description ships with (any <ul> except our own injected one). This is
   * the list shown before the customer has configured the product.
   *
   * @param {HTMLElement} descEl
   * @returns {HTMLUListElement|null}
   */
  function getBaseBulletList(descEl) {
    const lists = descEl.querySelectorAll('ul:not(.' + MARKER_CLASS + ')');
    return lists[lists.length - 1] || null;
  }

  /**
   * Swap between the authored base bullets and the variant spec bullets
   * based on configuration state:
   *   - Not fully configured → show the authored base bullets only.
   *   - Fully configured      → hide the base bullets, show the variant specs.
   *
   * The base list is hidden (never removed) so it returns when the customer
   * clears a selection or the section re-renders. If there are no variant
   * specs to show, the base bullets stay visible.
   */
  function applyVariantBullets(section, variantId, data) {
    const descEl = getDescriptionEl(section);
    if (!descEl) return;

    // Drop our previously-injected list so updates don't stack.
    const existingDynamic = descEl.querySelector('ul.' + MARKER_CLASS);
    if (existingDynamic) existingDynamic.remove();

    const baseList = getBaseBulletList(descEl);

    // Before full configuration: keep the authored base bullets visible.
    if (!shouldShowBullets(section)) {
      if (baseList) baseList.hidden = false;
      return;
    }

    // Fully configured: swap the base bullets out for the variant specs.
    const newUl = buildBulletList(variantId, data);
    if (newUl) {
      if (baseList) baseList.hidden = true;
      descEl.appendChild(newUl);
    } else if (baseList) {
      baseList.hidden = false;
    }
  }

  /**
   * Resolve the currently-active variant id for a section.
   * Priority: form input value > URL ?variant > data.selected.
   */
  function getCurrentVariantId(section, data) {
    const input = section.querySelector('product-form-component input[name="id"]');
    if (input && input.value) return input.value;

    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('variant');
    if (fromUrl) return fromUrl;

    return data.selected;
  }

  /**
   * Bind a single section. Idempotent — safe to call again after morph.
   */
  function init(section) {
    if (!section || section.dataset.dynamicDescriptionBound === 'true') return;

    const data = readData(section);
    if (!data) return;

    section.dataset.dynamicDescriptionBound = 'true';

    const sync = () => {
      const fresh = readData(section) || data;
      applyVariantBullets(section, getCurrentVariantId(section, fresh), fresh);
    };

    // Initial render — replaces the static authored <ul> with the dynamic one.
    sync();

    // Native variant picker — variant:update bubbles to the section after
    // the section morph completes.
    section.addEventListener('variant:update', () => {
      // Wait one frame so the form input has been updated by product-form.
      requestAnimationFrame(sync);
    });

    // Overflow picker dispatches this after matching a variant from
    // metafield/config dropdowns.
    section.addEventListener('variant:matched', sync);

    // Catch partial overflow changes (no variant:matched fires until a
    // full match exists, but we still want to re-evaluate visibility on
    // each partial pick — e.g. user fills 4 of 5 dropdowns then clears
    // one). Capture phase + tag check keeps this cheap.
    section.addEventListener(
      'change',
      (e) => {
        const target = e.target;
        if (!(target instanceof Element)) return;
        if (target.closest('variant-picker, overflow-variant-picker')) {
          requestAnimationFrame(sync);
        }
      },
      true
    );
  }

  function initAll() {
    document
      .querySelectorAll('.shopify-section:has([data-dynamic-description-data])')
      .forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor / morph re-renders may replace the section subtree.
  // Re-bind whenever a new data script appears.
  const observer = new MutationObserver(() => initAll());
  observer.observe(document.body, { childList: true, subtree: true });
})();
